import { useEffect, useRef, useState } from 'react';
import LoadedConditionSpeedChart from './LoadedConditionSpeedChart';
import LoadedConditionPercentDraughtChart from './LoadedConditionPercentDraughtChart';
import { AppDispatch, RootState } from '../../../../../core/store';
import { vesselSpeedAndDraughtValuesSelector } from '../../../kpiSelectors';
import { useAppDispatch, useAppSelector } from '../../../../../core/rootHooks';
import { kpisActions } from '../../../kpiSlice';
import { IKpiVessel } from '../../../kpiTypes';
import MapLibre from '../../../../../shared/components/maps/map-libre/MapLibre';
import LoadingSpinner from '../../../../../shared/components/loading-spinner/LoadingSpinner';
import { fixAntimeridian } from '../../../../../utils/mapHelpers';
import {
	selectVesselDailyPositionsAsLineString,
	selectVesselDailyPositionsWithoutLastSignalByImoNumber,
	selectVesselLastSignalInfoByImoNumber,
} from '../../../../main-map/mapSelectors';
import {
	getVesselOngoingVoyageSignalsRequestAction,
	selectVesselOngoingVoyageSignals,
} from '../../../../main-map/slices/vesselOngoingVoyageSignalsSlice';
import { Source, Layer } from 'react-map-gl';
import {
	lineLayerStyle,
	lineLayerStyleForDailyPositions,
	pointArrowLayerStyle,
	pointArrowLayerStyleForDailyPostitons,
	pointArrowLayerStyleForOngoingVoyage,
} from '../../../../../shared/components/maps/map-libre/mapLayers';
import { getVesselDailyPositionsRequestAction } from '../../../../main-map/slices/vesselDailyPositionsSlice';
import { getVesselOngoingVoyageDailySignalsRequestAction } from '../../../../main-map/slices/vesselOngoingVoyageDailySignalsSlice';
import { getVesselVoyageDailySignalsRequestAction } from '../../../../main-map/slices/vesselVoyageDailySignalsSlice';
import ExpandableContainer from '../../../../../shared/components/expandable-container/ExpandableContainer';
import {
	selectFeatureDateRangeAnalysisDateRange,
	selectFeatureDateRangeVesselLocationsDate,
} from '../../../../date-range/slices/dateRangeSlice';
import { convertCalendarDateToEndOfDay } from '../../../../date-range/utils/dateRangeHelpers';
import { selectVesselCurrentStatus } from '../../../../main-map/slices/vesselCurrentStatusSlice';

export interface IKpiStatusProps {
	vessel: IKpiVessel;
}

const KpiStatus: React.FC<IKpiStatusProps> = ({ ...props }) => {
	const dispatch = useAppDispatch<AppDispatch>();

	const ref = useRef<HTMLDivElement>(null);

	const [panelOffset, setPanelOffset] = useState<{ top: number; left: number }>();

	const vesselSpeedAndDraughtValues = useAppSelector((state: RootState) =>
		vesselSpeedAndDraughtValuesSelector(state, props.vessel.imoNumber!),
	);

	const vesselLastSignalInfo = useAppSelector((state: RootState) =>
		selectVesselLastSignalInfoByImoNumber(state, props.vessel.imoNumber!),
	);
	const vesselOngoingVoyageSignals = useAppSelector(selectVesselOngoingVoyageSignals);
	
	const vesselDailyPositionsWithoutLastSignal = useAppSelector((state: RootState) =>
		selectVesselDailyPositionsWithoutLastSignalByImoNumber(state, props.vessel.imoNumber),
	);
	const vesselDailyPositionsAsLineString = useAppSelector(selectVesselDailyPositionsAsLineString);
	const { vesselOngoingVoyageDailySignalsData } = useAppSelector(
		(state: RootState) => state.vesselOngoingVoyageDailySignalsSlice,
	);

	const vesselLocationsDate = useAppSelector(selectFeatureDateRangeVesselLocationsDate);
	const analysisDateRange = useAppSelector(selectFeatureDateRangeAnalysisDateRange);

	const vesselCurrentStatus = useAppSelector(selectVesselCurrentStatus);

	useEffect(() => {
		if (props.vessel.imoNumber) {
			const startDate = analysisDateRange.dateRange!.start.toDate('UTC').toISOString();
			const endDate = analysisDateRange.dateRange!.end.toDate('UTC').toISOString();

			dispatch(
				kpisActions.getVesselSpeedAndDraughtOnLoadedConditionsRequestAction({
					imoNumber: props.vessel.imoNumber,
					startDate,
					endDate,
				}),
			);
		}
	}, [dispatch, props.vessel.imoNumber, analysisDateRange]);

	useEffect(() => {
		if (props.vessel.imoNumber) {
			const date = vesselLocationsDate.specificDate
				? convertCalendarDateToEndOfDay(vesselLocationsDate.specificDate)
				: new Date();

			const startDate = analysisDateRange.dateRange!.start.toDate('UTC').toISOString();
			const endDate = analysisDateRange.dateRange!.end.toDate('UTC').toISOString();

			dispatch(getVesselOngoingVoyageSignalsRequestAction({ imoNumber: props.vessel.imoNumber, date: endDate }));
			dispatch(getVesselVoyageDailySignalsRequestAction({ imoNumbers: [props.vessel.imoNumber], startDate, endDate }));
			dispatch(getVesselDailyPositionsRequestAction({ imoNumbers: [props.vessel.imoNumber], date: date.toISOString() }));
			dispatch(getVesselOngoingVoyageDailySignalsRequestAction({ imoNumbers: [props.vessel.imoNumber], date: endDate }));
		}
	}, [dispatch, props.vessel.imoNumber, analysisDateRange]);

	useEffect(() => {
		const updatePanelSize = () => {
			if (ref.current) {
				const { offsetTop, offsetLeft } = ref.current;
				setPanelOffset({ top: offsetTop, left: offsetLeft });
			}
		};

		// Initial measurement
		updatePanelSize();

		// Update on window resize
		window.addEventListener('resize', updatePanelSize);
		return () => {
			window.removeEventListener('resize', updatePanelSize);
		};
	}, []);

	return (
		<div ref={ref} className="flex flex-row w-full gap-4">
			<div className="flex flex-col basis-1/3 gap-4">
				<div className="flex flex-col basis-1/4 border border-msi-darkgrey-color-300 rounded p-2">
					<h2 className="font-bold text-xl mb-2">Status</h2>

					<div className="text-base space-y-3">
						<div>
							<strong>Last update : </strong>
							{vesselCurrentStatus?.timestamp && new Date(vesselCurrentStatus?.timestamp).toLocaleString()}
						</div>
						<div>
							<strong>Course / Direction : </strong>
							{`${vesselCurrentStatus?.heading || 0}°`}
						</div>
						<div>
							<strong>Latitude / Longitude : </strong>
							{`${(vesselCurrentStatus?.longitude && vesselCurrentStatus?.longitude.toFixed(5)) || 0}° / ${(vesselCurrentStatus?.latitude && vesselCurrentStatus?.latitude.toFixed(5)) || 0}°`}
						</div>
						<div>
							<strong>Laden Status : </strong>{' '}
							{!vesselCurrentStatus || vesselCurrentStatus?.ladenCount === 0 ? 'Not Laden' : 'Laden'}
						</div>
						<div>
							<strong>Reported Destination : </strong>
							{vesselCurrentStatus?.reportedDestination}
						</div>
						<div>
							<strong>Reported ETA : </strong>
							{vesselCurrentStatus?.reportedEta && new Date(vesselCurrentStatus?.reportedEta).toLocaleString()}
						</div>
					</div>
				</div>
				<div className="flex flex-col basis-3/4 border border-msi-darkgrey-color-300 rounded p-1">
					{
						<MapLibre
							center={{
								longitude: vesselLastSignalInfo?.geometry?.coordinates?.[0] || 0,
								latitude: vesselLastSignalInfo?.geometry?.coordinates?.[1] || 0,
							}}
						>
							{vesselDailyPositionsWithoutLastSignal?.features && (
								<Source id="vesselDailyPositions" type="geojson" data={vesselDailyPositionsWithoutLastSignal}>
									<Layer {...pointArrowLayerStyleForDailyPostitons()} type="symbol" />
								</Source>
							)}

							{vesselDailyPositionsAsLineString?.features && (
								<Source id="vesselDailyPositionsAsLine" type="geojson" data={fixAntimeridian(vesselDailyPositionsAsLineString)}>
									<Layer {...lineLayerStyleForDailyPositions()} type="line" />
								</Source>
							)}

							{vesselOngoingVoyageSignals &&
								vesselOngoingVoyageSignals.vesselOngoingVoyageSignalsData &&
								vesselOngoingVoyageSignals.vesselOngoingVoyageSignalsData.features && (
									<Source
										id="lines"
										type="geojson"
										data={fixAntimeridian(vesselOngoingVoyageSignals.vesselOngoingVoyageSignalsData)}
									>
										<Layer {...lineLayerStyle()} type="line" />
									</Source>
								)}

							{vesselOngoingVoyageDailySignalsData && (
								<Source id="vesselOngoingVoyageDailySignals" type="geojson" data={vesselOngoingVoyageDailySignalsData}>
									<Layer {...pointArrowLayerStyleForOngoingVoyage()} type="symbol" />
								</Source>
							)}

							<Source id="points" type="geojson" data={vesselLastSignalInfo}>
								<Layer {...pointArrowLayerStyle(5)} type="symbol" />
							</Source>
						</MapLibre>
					}
				</div>
			</div>
			<div className="flex flex-col basis-2/3 gap-4">
				<div className="basis-1/2 w-full border border-msi-darkgrey-color-300 rounded">
					{!vesselSpeedAndDraughtValues || vesselSpeedAndDraughtValues?.loading ? (
						<LoadingSpinner size="md" spinnerClassName="p-2" />
					) : (
						<ExpandableContainer panelOffsets={panelOffset}>
							<LoadedConditionSpeedChart vesselSpeedAndDraughtValues={vesselSpeedAndDraughtValues} />
						</ExpandableContainer>
					)}
				</div>
				<div className="basis-1/2 w-full border border-msi-darkgrey-color-300 rounded">
					{!vesselSpeedAndDraughtValues || vesselSpeedAndDraughtValues?.loading ? (
						<LoadingSpinner size="md" spinnerClassName="p-2" />
					) : (
						<ExpandableContainer panelOffsets={panelOffset}>
							<LoadedConditionPercentDraughtChart vesselSpeedAndDraughtValues={vesselSpeedAndDraughtValues} />{' '}
						</ExpandableContainer>
					)}
				</div>
			</div>
		</div>
	);
};

export default KpiStatus;
